import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { handleNewsletterSubscription } from '../mock';
import { toast } from 'sonner';
import { openBookingPopup } from '../utils/bookingPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: ''
  });

  // Fetch social links on mount
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/social-links`);
        if (res.ok) {
          const data = await res.json();
          setSocialLinks({
            facebook: data.facebook || '',
            instagram: data.instagram || '',
            linkedin: data.linkedin || '',
            twitter: data.twitter || ''
          });
        }
      } catch (error) {
        console.log('Using default social links');
      }
    };
    fetchSocialLinks();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const result = await handleNewsletterSubscription(email);
      toast.success(result.message);
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerSections = [
    {
      title: 'Solutions',
      links: [
        { label: 'NDIS Providers', path: '/solutions' },
        { label: 'SIL Providers', path: '/solutions' },
        { label: 'Support Coordinators', path: '/solutions' },
        { label: 'Plan Managers', path: '/solutions' },
        { label: 'Allied Health', path: '/solutions' }
      ]
    },
    {
      title: 'Platform',
      links: [
        { label: 'Features & Pricing', path: '/features-pricing' },
        { label: 'ROI Calculator', path: '/roi-calculator' },
        { label: 'AI Capabilities', path: '/features-pricing' },
        { label: 'Case Studies', path: '/case-studies' },
        { label: 'Security', path: '/about' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', path: '/about' },
        { label: 'Partnerships', path: '/about' },
        { label: 'Contact', path: '/contact' },
        { label: 'Book Demo', path: '/book' }
      ]
    }
  ];

  return (
    <footer className="bg-primary-navy text-white" data-testid="main-footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/getcare360-logo.png" 
                alt="GetCare 360" 
                className="h-8 w-auto"
              />
            </Link>
            <p className="text-white/70 mb-6 leading-relaxed">
              Australia's most advanced custom AI platform for NDIS providers. 
              Built for your workflow, powered by AI.
            </p>
            <div className="flex space-x-4">
              {socialLinks.linkedin && (
                <a 
                  href={socialLinks.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-care-teal flex items-center justify-center transition-colors" 
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                >
                  <Linkedin size={18} />
                </a>
              )}
              {socialLinks.twitter && (
                <a 
                  href={socialLinks.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-care-teal flex items-center justify-center transition-colors" 
                  aria-label="X (Twitter)"
                  data-testid="social-twitter"
                >
                  <Twitter size={18} />
                </a>
              )}
              {socialLinks.facebook && (
                <a 
                  href={socialLinks.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-care-teal flex items-center justify-center transition-colors" 
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook size={18} />
                </a>
              )}
              {socialLinks.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-care-teal flex items-center justify-center transition-colors" 
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram size={18} />
                </a>
              )}
              {/* Show placeholder icons if no links are set */}
              {!socialLinks.linkedin && !socialLinks.twitter && !socialLinks.facebook && !socialLinks.instagram && (
                <>
                  <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center opacity-50" title="LinkedIn (not configured)">
                    <Linkedin size={18} />
                  </span>
                  <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center opacity-50" title="X (not configured)">
                    <Twitter size={18} />
                  </span>
                  <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center opacity-50" title="Facebook (not configured)">
                    <Facebook size={18} />
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h4 className="font-semibold text-lg mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.label === 'Book Demo' ? (
                      <button 
                        onClick={openBookingPopup}
                        className="text-white/70 hover:text-care-teal transition-colors cursor-pointer bg-transparent border-none p-0"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link 
                        to={link.path}
                        className="text-white/70 hover:text-care-teal transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-lg mb-2">Stay Updated</h4>
              <p className="text-white/70 mb-4">
                Get the latest insights on NDIS compliance, AI in healthcare, and platform updates.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-care-teal"
                required
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-tech-blue hover:bg-[#1558C9] text-white px-8"
                data-testid="newsletter-submit"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-8 pt-8 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Mail className="text-care-teal mt-1" size={20} />
              <div>
                <p className="font-medium">Email</p>
                <a href="mailto:info@getcare360.com.au" className="text-white/70 hover:text-care-teal transition-colors">
                  info@getcare360.com.au
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="text-care-teal mt-1" size={20} />
              <div>
                <p className="font-medium">Phone</p>
                <a href="tel:1800951438" className="text-white/70 hover:text-care-teal transition-colors">
                  1800 951 GET
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <MapPin className="text-care-teal mt-1" size={20} />
              <div>
                <p className="font-medium">Location</p>
                <p className="text-white/70">Australia Wide</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/60 text-sm">
              © 2026 GetCare 360. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-white/60 hover:text-care-teal transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-white/60 hover:text-care-teal transition-colors">
                Terms of Service
              </Link>
              <Link to="/cookie-policy" className="text-white/60 hover:text-care-teal transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
